import api from './api';

/**
 * Notification Settings API Service
 * 
 * Handles all club notification settings management:
 * - Global enable/disable controls
 * - Category-level bulk operations
 * - Individual notification type settings
 * - Channel configuration (Email/In-App)
 * - Quiet hours management
 * - Settings summary and statistics
 */
export const notificationSettingsApi = {
  /**
   * Get all notification settings for a club
   * @param {string} clubId - Club ID
   * @returns {Promise} API response with complete settings
   */
  getSettings: (clubId) => {
    return api.get(`/api/clubs/${clubId}/notification-settings`);
  },

  /**
   * Get settings summary with statistics
   * @param {string} clubId - Club ID
   * @returns {Promise} API response with summary stats
   */
  getSettingsSummary: (clubId) => {
    return api.get(`/api/clubs/${clubId}/notification-settings/summary`);
  },

  /**
   * Update global notification settings (master on/off)
   * @param {string} clubId - Club ID
   * @param {Object} globalSettings - Global settings object
   * @param {boolean} globalSettings.allNotificationsEnabled - Master enable/disable
   * @returns {Promise} API response
   */
  updateGlobalSettings: (clubId, globalSettings) => {
    return api.put(`/api/clubs/${clubId}/notification-settings/global`, globalSettings);
  },

  /**
   * Update category-level settings (bulk enable/disable)
   * @param {string} clubId - Club ID
   * @param {string} category - Category name (club, program, event, etc.)
   * @param {Object} categorySettings - Category settings
   * @param {boolean} categorySettings.categoryEnabled - Enable/disable entire category
   * @param {Object} categorySettings.channels - Default channels for category
   * @returns {Promise} API response
   */
  updateCategorySettings: (clubId, category, categorySettings) => {
    return api.put(`/api/clubs/${clubId}/notification-settings/category/${category}`, categorySettings);
  },

  /**
   * Update individual notification type settings
   * @param {string} clubId - Club ID
   * @param {string} type - Notification type
   * @param {Object} typeSettings - Type-specific settings
   * @param {boolean} typeSettings.enabled - Enable/disable this notification
   * @param {Object} typeSettings.channels - Channel configuration
   * @param {boolean} typeSettings.channels.inApp - In-app notifications
   * @param {boolean} typeSettings.channels.email - Email notifications
   * @param {Array} typeSettings.reminders - Reminder configuration
   * @returns {Promise} API response
   */
  updateNotificationTypeSettings: (clubId, type, typeSettings) => {
    return api.put(`/api/clubs/${clubId}/notification-settings/type/${type}`, typeSettings);
  },

  /**
   * Update quiet hours configuration
   * @param {string} clubId - Club ID
   * @param {Object} quietHours - Quiet hours configuration
   * @param {boolean} quietHours.enabled - Enable quiet hours
   * @param {string} quietHours.startTime - Start time (HH:MM format)
   * @param {string} quietHours.endTime - End time (HH:MM format)
   * @param {Array} quietHours.affectedChannels - Which channels are affected
   * @param {string} quietHours.timezone - Timezone for quiet hours
   * @returns {Promise} API response
   */
  updateQuietHours: (clubId, quietHours) => {
    return api.put(`/api/clubs/${clubId}/notification-settings/quiet-hours`, quietHours);
  },

  /**
   * Bulk update multiple notification types
   * @param {string} clubId - Club ID
   * @param {Array} updates - Array of notification updates
   * @returns {Promise} API response
   */
  bulkUpdateNotifications: (clubId, updates) => {
    return api.put(`/api/clubs/${clubId}/notification-settings/bulk`, { updates });
  },

  /**
   * Reset settings to defaults
   * @param {string} clubId - Club ID
   * @param {string} scope - Reset scope ('all', 'category', 'type')
   * @param {string} target - Target category or type (if scope is not 'all')
   * @returns {Promise} API response
   */
  resetToDefaults: (clubId, scope = 'all', target = null) => {
    const params = { scope };
    if (target) params.target = target;
    
    return api.post(`/api/clubs/${clubId}/notification-settings/reset`, params);
  }
};

/**
 * Default notification settings structure
 */
export const DEFAULT_NOTIFICATION_SETTINGS = {
  globalSettings: {
    allNotificationsEnabled: true,
    defaultChannels: {
      inApp: true,
      email: true
    }
  },
  categories: {
    club: {
      categoryEnabled: true,
      notifications: {}
    },
    program: {
      categoryEnabled: true,
      notifications: {}
    },
    event: {
      categoryEnabled: true,
      notifications: {}
    },
    rsvp: {
      categoryEnabled: true,
      notifications: {}
    },
    equipment: {
      categoryEnabled: true,
      notifications: {}
    },
    team: {
      categoryEnabled: true,
      notifications: {}
    },
    assignment: {
      categoryEnabled: true,
      notifications: {}
    }
  },
  quietHours: {
    enabled: false,
    startTime: '22:00',
    endTime: '08:00',
    affectedChannels: ['email'],
    timezone: 'local'
  },
  lastModified: new Date().toISOString(),
  version: '1.0'
};

/**
 * Channel configuration options
 */
export const NOTIFICATION_CHANNELS = {
  IN_APP: {
    key: 'inApp',
    name: 'In-App',
    icon: '📱',
    description: 'Push notifications within the app'
  },
  EMAIL: {
    key: 'email',
    name: 'Email',
    icon: '📧',
    description: 'Email notifications to user\'s email address'
  },
  SMS: {
    key: 'sms',
    name: 'SMS',
    icon: '📱',
    description: 'Text message notifications (coming soon)'
  }
};

/**
 * Quiet hours presets
 */
export const QUIET_HOURS_PRESETS = [
  {
    name: 'Night Hours',
    startTime: '22:00',
    endTime: '08:00',
    description: 'Standard overnight quiet period'
  },
  {
    name: 'Business Hours',
    startTime: '09:00',
    endTime: '17:00',
    description: 'During work hours'
  },
  {
    name: 'Weekend Mornings',
    startTime: '22:00',
    endTime: '10:00',
    description: 'Extended quiet hours for weekends'
  },
  {
    name: 'Custom',
    startTime: '00:00',
    endTime: '00:00',
    description: 'Set your own times'
  }
];

/**
 * Helper functions for settings management
 */
export const settingsHelpers = {
  /**
   * Calculate enabled notifications count
   * @param {Object} settings - Notification settings
   * @returns {Object} Count statistics
   */
  getEnabledCount: (settings) => {
    let totalEnabled = 0;
    let totalAvailable = 0;
    const categoryStats = {};

    Object.entries(settings.categories || {}).forEach(([categoryKey, category]) => {
      let categoryEnabled = 0;
      let categoryTotal = 0;

      Object.entries(category.notifications || {}).forEach(([type, notification]) => {
        categoryTotal++;
        totalAvailable++;
        
        if (notification.enabled && category.categoryEnabled && settings.globalSettings?.allNotificationsEnabled) {
          categoryEnabled++;
          totalEnabled++;
        }
      });

      categoryStats[categoryKey] = {
        enabled: categoryEnabled,
        total: categoryTotal,
        percentage: categoryTotal > 0 ? Math.round((categoryEnabled / categoryTotal) * 100) : 0
      };
    });

    return {
      total: {
        enabled: totalEnabled,
        available: totalAvailable,
        percentage: totalAvailable > 0 ? Math.round((totalEnabled / totalAvailable) * 100) : 0
      },
      categories: categoryStats
    };
  },

  /**
   * Check if notification is effectively enabled
   * @param {Object} settings - Notification settings
   * @param {string} category - Category name
   * @param {string} type - Notification type
   * @returns {boolean} True if notification will be sent
   */
  isNotificationEnabled: (settings, category, type) => {
    if (!settings.globalSettings?.allNotificationsEnabled) return false;
    if (!settings.categories?.[category]?.categoryEnabled) return false;
    return settings.categories?.[category]?.notifications?.[type]?.enabled || false;
  },

  /**
   * Get effective channels for a notification
   * @param {Object} settings - Notification settings
   * @param {string} category - Category name
   * @param {string} type - Notification type
   * @returns {Object} Effective channel configuration
   */
  getEffectiveChannels: (settings, category, type) => {
    const notification = settings.categories?.[category]?.notifications?.[type];
    const defaultChannels = settings.globalSettings?.defaultChannels || { inApp: true, email: true };
    
    return notification?.channels || defaultChannels;
  },

  /**
   * Check if quiet hours are active
   * @param {Object} quietHours - Quiet hours configuration
   * @param {Date} currentTime - Current time (defaults to now)
   * @returns {boolean} True if currently in quiet hours
   */
  isQuietHoursActive: (quietHours, currentTime = new Date()) => {
    if (!quietHours?.enabled) return false;

    const now = currentTime;
    const startTime = new Date(now);
    const endTime = new Date(now);

    // Parse time strings (HH:MM)
    const [startHour, startMin] = quietHours.startTime.split(':').map(Number);
    const [endHour, endMin] = quietHours.endTime.split(':').map(Number);

    startTime.setHours(startHour, startMin, 0, 0);
    endTime.setHours(endHour, endMin, 0, 0);

    // Handle overnight quiet hours (e.g., 22:00 to 08:00)
    if (endTime <= startTime) {
      endTime.setDate(endTime.getDate() + 1);
    }

    return now >= startTime && now <= endTime;
  }
};
